<template>
  <div class="w-full h-full pt-5">
    <div class="flex">
      <h1 class="text-xl text-left font-extrabold pl-4">Organisation Design</h1>
      <div class="w-7/12 mt-1 ml-4">
        <Breadcrumb :items="breadcrumbs" />
      </div>
    </div>
    <div v-if="tabsReady" class="mx-3 mt-6">
      <Tabs
        class="mx-4"
        :tabs="tabsItems"
        :active-tab="activeTab"
        @currentTab="onTabChange($event)"
        :border="true"
      />
    </div>
    <div>
      <div class="mx-3 mt-6">
        <Card class="mt-6 p-5">
          <div class="flex gap-2 mb-6" style="justify-content: flex-end">
            <Button class="text-white" :background="appearance.buttonColor || defaultBTNColor" @click="handleClick">
              {{ 
                activeTab === 'Position' ? 'Add New' :
                this.isJobLevel ? 'Add Role' : 'Add Post' }}
            </Button>
            <Button
              v-if="activeTab !== 'Position'"
              class="text-dynamicBackBtn bg-white border border-dynamicBackBtn"
              @click="handleImport"
            >
              Import
            </Button>
          </div>
          <template v-if="!showError" v-slot:footer>
            <card-footer printcard reloadcard @print="print" @reload="reload()" @sortType="handleSort($event)"
              @option="handleOptionSelect($event)" @searchResult="handleSearch($event)" />
          </template>
        </Card>
      </div>
      <div v-if="!showError" class="p-3">
        <sTable v-if="activeTab === 'Position'" :headers="designationHeader" :items="designationArray" style="width: 100%" aria-label="designationTable"
          :pagination-list="metaData" id="printMe" @page="handlePage($event)" @itemsPerPage="handleItemsPerPage($event)"
          :loading="loadingData" class="w-full" has-number>
          <template v-slot:item="{ item }">
            <span v-if="item.name">
              {{ item.data.name || "------" }}
            </span>
            <span v-else-if="item.orgFunction">
              {{ item.data.orgFunction ? item.data.orgFunction.name : "------" }}
            </span>
            <span v-else-if="item.positionCode">
              {{ item.data.positionCode || "------" }}
            </span>
            <span v-else-if="item.tenure">
              {{ item.data.tenure || "------" }}
            </span>
            <span v-else-if="item.employmentType">
              {{ item.data.employmentType || "------" }}
            </span>
            <span v-else-if="item.reportsTo">
              {{ item.data.reportsTo ? item.data.reportsTo.name : "------" }}
            </span>
            <span v-else-if="item.headCountBadge">
              {{ item.data.headCountBadge ?? "------" }}
            </span>
            <span v-else-if="item.id">
              <Menu left top="-150" margin="24" class="my-2 p-0">
                <template v-slot:title>
                  <icon icon-name="more_icon" size="xs" />
                </template>
                <div style="min-width: 180px; min-height: 81px" class="py-3 px-2">
                  <div class="flex py-1 px-3 more h-8 cursor-pointer" @click="handleEdit(item.data.id)">
                    <icon icon-name="edit" class-name="text-blueCrayola mr-2" size="xs" />
                    <p class="pt-1">Edit</p>
                  </div>
                  <div
                    v-if="item.data.jobTitle"
                    class="flex py-1 px-3 more h-8 cursor-pointer"
                    @click="handlePayBand(item.data)"
                  >
                    <icon icon-name="icon-eye" class-name="text-darkPurple mr-2" size="xs" />
                    <p class="pt-1">View Payband</p>
                  </div>
                  <div class="flex px-3 py-1 more h-8 cursor-pointer" @click="confirmDelete(item.data.id)">
                    <icon icon-name="delete" class-name="text-desire mr-2" size="xsm" />
                    <p class="pt-1">Delete</p>
                  </div>
                </div>
              </Menu>
            </span>
          </template>
        </sTable>
        <NonPositionPanel
          v-else-if="activeTab !== 'Position'"
          :designation-titles="filteredDesignationTitles"
          :active-tab="activeTab"
          :compensation-model="compensationModel"
          :loading="loadingData"
          @edit="handleNonPositionEdit"
          @delete="confirmNonPositionDelete"
        />
      </div>
      <div v-else>
        <error-page />
      </div>
    </div>

    <RightSideBar
      v-if="openPayBandModal"
      close-button
      :loading="loadingPayBand"
      @close="closePayBandModal"
    >
      <template v-slot:title>
        <p class="text-darkPurple">
          PayInfo
        </p>
      </template>
      <template>
        <div
          v-if="!payBandSetup"
          class="flex flex-col items-center justify-center gap-4 px-6 py-10 mt-4 bg-white rounded-xl border text-center"
        >
          <Icon
            icon-name="paygrade_Illustration"
            size=""
            class-name="w-40 h-40"
          />
          <div class="space-y-2">
            <p class="text-sm font-semibold text-darkPurple">Pay info unavailable</p>
            <p class="text-sm text-gray-600 max-w-xs leading-5">
              {{ bandError }}
            </p>
          </div>
        </div>
        <template v-else>
          <h2 v-if="payBandHeader" class="text-gray-800 font-semibold text-sm">
            {{ payBandHeader.title }} -
            <span class="text-darkPurple font-bold uppercase">{{ payBandHeader.level }}</span>
          </h2>
          <div class="p-6 w-full bg-white rounded-xl border mt-4 space-y-8">
            <div v-for="(item, index) in roles" :key="index" class="rounded-lg space-y-1">
              <div class="flex justify-between items-center">
                <p class="font-semibold text-sm text-gray-700">
                  {{ item.title }} -
                  <span class="text-dynamicBackBtn font-black uppercase">{{ item.grade }}</span>
                </p>
              </div>
              <div class="text-xs text-gray-500 flex items-center gap-1 mb-5">
                <Icon icon-name="icon-location" class="text-gray-500" size="xs" />
                <span>{{ item.location }}</span>
              </div>

              <div>
                <p class="text-xs font-bold mb-1 uppercase mt-5">Pay Info</p>
                <div class="flex flex-col gap-1 text-sm mt-1 bg-gray-100 px-2 py-2 rounded-lg w-fit">
                  <div class="flex gap-1">
                    <Icon icon-name="refund-line" size="xs" />
                    <span class="text-black font-bold text-sm">{{ item.payLabel }}</span>
                  </div>
                  <span class="text-gray-700">{{ item.payRange }}</span>
                </div>
              </div>
            </div>
          </div>
        </template>
      </template>
    </RightSideBar>


    <RightSideBar v-if="openModal" @submit="editMode ? updateDesignation() : createDesignation()"
      @close="(openModal = false), reset()" submit="Save" :loading="loading"
      :button-class="`bg-dynamicBackBtn text-white`" :disabled-button="disableBtn">
      <template v-slot:title>
        <p class="text-darkPurple">
          {{ editMode ? "Edit Position" : "New Position" }}
        </p>
      </template>
      <template v-slot:subtitle>
        <p class="font-semibold text-darkPurple">
          Which of these two best describes the Position?
        </p>
        <radio-button :options="categoryArray" col-span="col-span-6" class="m-4 text-darkPurple" row-gap="gap-y-5"
          v-model="designationCredentials.isSupervisory" @input="setIsSupervisory" />
      </template>
      <div>
        <p class="text-darkPurple font-semibold">Position Details</p>
        <div class="grid grid-cols-12 gap-2 gap-y-4 text-darkPurple" style="margin-top: 1.125rem">
          <div v-if="!isJobLevel" class="col-span-12">
            <c-select
              placeholder="--Select--"
              variant="w-full"
              label="Post Type"
              :options="postTypeOptions"
              v-model="designationCredentials.postType"
            />
          </div>
          <div v-if="isJobLevel" class="col-span-12">
            <c-select
              placeholder="--Select--"
              variant="w-full"
              label="Job Family"
              :options="jobFamilyOptions"
              v-model="designationCredentials.jobClassificationId"
            />
          </div>
          <div
            v-else-if="designationCredentials.postType !== 'Administrative Post'"
            class="col-span-12"
          >
            <c-select
              placeholder="--Select--"
              variant="w-full"
              label="Job Cadre"
              :options="jobCadreOptions"
              v-model="designationCredentials.jobClassificationId"
            />
          </div>
          <div class="col-span-12">
            <c-select
              :key="jobTitleSelectKey"
              placeholder="--Select--"
              variant="w-full"
              :label="postNameLabel"
              :options="jobTitleOptions"
              v-model="designationCredentials.jobTitleId"
              @input="onJobTitleSelect"
            />
          </div>
          <div class="col-span-12">
            <c-select
              placeholder="--Select--"
              :options="functions"
              @input="onOrganisationUnitSelect"
              label="Organisation Unit"
              variant="w-full"
              v-model="designationCredentials.functionId"
            />
          </div>
          <div class="col-span-12">
            <c-text
              placeholder="Autoloaded"
              disabled
              label="Position Code"
              variant="w-full"
              v-model="designationCredentials.positionCode"
            />
          </div>
          <div class="col-span-12">
            <c-text
              placeholder="Autoloaded"
              disabled
              label="Cost Center"
              variant="w-full"
              v-model="designationCredentials.costCentre"
            />
          </div>
          <div v-if="isJobLevel" class="col-span-12">
            <c-select
              placeholder="--Select--"
              :options="locationData"
              @input="formattedLocationTags($event)"
              label="Location (Optional)"
              variant="w-full"
              v-model="designationCredentials.locationIds[0]"
            />
          </div>
          <div class="col-span-12">
            <c-select
              placeholder="--Select--"
              :options="reportingLine"
              variant="w-full"
              label="Reports To"
              v-model="designationCredentials.reportingTo"
            />
          </div>
          <div class="col-span-12">
            <c-select
              placeholder="--Select--"
              variant="w-full"
              label="Tenure"
              :options="positionTenureOptions"
              v-model="designationCredentials.tenure"
            />
          </div>
          <div v-if="isNonSupervisory" class="col-span-12">
            <c-text
              placeholder="--Enter--"
              label="Head Count Budget"
              variant="w-full"
              v-model="designationCredentials.headCountBadge"
            />
          </div>
          <div v-else class="col-span-12">
            <c-text
              placeholder="Autoloaded"
              disabled
              label="Head Count Budget"
              variant="w-full"
              v-model="designationCredentials.headCountBadge"
            />
          </div>
          <div class="col-span-12">
            <c-select
              placeholder="--Select--"
              variant="w-full"
              label="Employment Type (Optional)"
              :options="employmentTypeOptions"
              v-model="designationCredentials.employmentType"
            />
          </div>
          <div class="col-span-12">
            <c-select
              placeholder="--Select--"
              variant="w-full"
              label="Work Mode (Optional)"
              :options="workModeOptions"
              v-model="designationCredentials.workMode"
            />
          </div>
          <div class="col-span-12">
            <c-text
              placeholder="--Enter--"
              label="FTE (Optional)"
              variant="w-full"
              v-model="designationCredentials.fte"
            />
          </div>
          <div class="col-span-12">
            <c-select
              placeholder="--Select--"
              :options="overtimeOptions"
              label="Eligible for Overtime?"
              variant="w-full"
              v-model="designationCredentials.eligibleForOvertime"
            />
          </div>
          <div class="col-span-12">
            <checkbox
              label="Set up dotted reporting for this post."
              class="cursor-pointer"
              checkbox-size="height:16px; width:16px; margin-top:4px;"
              label-style="color:#333333"
              @change="handleDottedReporting"
              v-model="isDottedReportingTo"
            />
          </div>
          <div class="col-span-12" v-if="isDotted">
            <c-select
              placeholder="--Select--"
              :options="supervisoryDesignation"
              variant="w-full"
              label="Dotted Reporting line"
              v-model="designationCredentials.dottedReportingTo"
            />
          </div>
          <p class="col-span-12 text-flame">
            {{ error }}
          </p>
        </div>
      </div>
    </RightSideBar>

    <NonPositionDrawer
      :is-open="openNonPositionModal"
      :is-edit="editNonPositionMode"
      :active-tab="activeTab"
      :is-job-level="isJobLevel"
      :initial-data="selectedNonPositionTitle"
      :job-classifications="jobClassificationsOpt"
      :levels="levelsArray"
      :job-grades="jobGrades"
      :compensation-model="compensationModel"
      :is-educational-institution="isEducationalInstitution"
      :loading="loading"
      @close="closeNonPositionDrawer"
      @save="handleSaveNonPosition"
    />

    <Modal v-if="dialogDel">
      <Card class="p-5 flex flex-col max-w-md">
        <div class>
          <p class="font-bold ml-4 my-5 text-center">
            Are you sure you want to delete this Designation?
          </p>
          <div class="flex">
            <Button class="text-white" :background="appearance.buttonColor || defaultBTNColor"
              @click="deleteDesignation">
              Yes
            </Button>
            <Button background-color="none" class="text-dynamicBackBtn border border-solid border-dynamicBackBtn ml-4"
              @click="dialogDel = false">
              Cancel
            </Button>
          </div>
        </div>
      </Card>
    </Modal>
    <import-employee :open-modal="isOpenImport" @close="isOpenImport = false" :isDisplayProgressBar="false"
      :isOverwriteEmployee="false" :isTemplate="true" @download="downloadDesignationTemplate()"
      @reload-employees="reload()" @importEmployee="importDesignation" :url="importUploadUrl"
      :title="`Import ${this.isJobLevel ? 'Job Role' : 'Post'} Data`" :is-employee-directory="false"
    />
  </div>
</template>

<script>
import { mapState } from "vuex";
import Breadcrumb from "@scelloo/cloudenly-ui/src/components/bread-crumb";
import Tabs from "@scelloo/cloudenly-ui/src/components/tab";
import CText from "@scelloo/cloudenly-ui/src/components/text";
import { clone } from "lodash";
import STable from "@scelloo/cloudenly-ui/src/components/table";
import CSelect from "@scelloo/cloudenly-ui/src/components/select";
import ErrorPage from "@/modules/Admin/error403";
import CardFooter from "@/components/CardFooter";
import Card from "@/components/Card";
import Button from "@/components/Button";
import Menu from "@/components/Menu";
import RightSideBar from "@/components/RightSideBar";
import RadioButton from "@/components/RadioButton";
import Modal from "@/components/Modal";
import ComboBox from "@/components/ComboBox";
import Checkbox from "@/components/Checkbox";
import ImportEmployee from "@/components/importEmployee";
import NonPositionPanel from "./NonPositionPanel.vue";
import NonPositionDrawer from "./NonPositionDrawer.vue";
import VSelect from "vue-select";
import "vue-select/dist/vue-select.css";

export default {
  name: "Designation",
  components: {
    Button,
    Card,
    Checkbox,
    CardFooter,
    ComboBox,
    Breadcrumb,
    Tabs,
    ErrorPage,
    CText,
    CSelect,
    Menu,
    STable,
    RightSideBar,
    Modal,
    RadioButton,
    ImportEmployee,
    NonPositionPanel,
    NonPositionDrawer,
    VSelect,
  },
  data() {
    return {
      isEducationalInstitution: false,
      isLoadingPositionForm: false,
      jobTitleSelectKey: 0,
      isOpenImport: false,
      isDottedReportingTo: false,
      disableBtn: false,
      designationArray: [],
      bandError: "",
      openModal: false,
      openNonPositionModal: false,
      editNonPositionMode: false,
      selectedNonPositionTitle: null,
      openPayBandModal: false,
      loadingPayBand: false,
      editMode: false,
      showError: false,
      dialogDel: false,
      designationId: "",
      isDeletingNonPositionTitle: false,
      isDotted: false,
      min: "",
      max: "",
      bandName: "",
      loadingData: true,
      loading: false,
      itemsPerPage: null,
      numberOfPage: null,
      searchWord: null,
      isActive: null,
      sortType: null,
      metaData: {},
      roles: [],
      payBandHeader: null,
      functions: [],
      designations: [],
      designationTitles: [],
      reportingLine: [],
      compensationModel: "",
      hierarchy: [],
      levelsArray: [],
      getLevel: "",
      minLevelId: "",
      payBandSetup: false,
      payRange: null,
      payGrade: null,
      timeout: null,
      error: "",
      locationData: [],
      jobGrades: [],
      jobClassificationsOpt: [],
      designationCredentials: {
        name: "",
        isSupervisory: true,
        functionId: null,
        reportingTo: null,
        dottedReportingTo: null,
        enabledOvertime: null,
        eligibleForOvertime: null,
        levelId: null,
        costCentre: null,
        locationIds: [],
        jobGradeIds: [],
        jobClassificationId: null,
        maximumLevelId: null,
        minimumLevelId: null,
        postType: null,
        jobTitleId: null,
        positionCode: "",
        tenure: null,
        headCountBadge: 1,
        employmentType: null,
        workMode: null,
        fte: null,
      },
      tenureOptions: [
        { id: "Permanent", name: "Permanent" },
        { id: "Contract", name: "Contract" },
        { id: "Temporary", name: "Temporary" },
      ],
      nonSupervisoryTenureOptions: [
        { id: "Fixed", name: "Fixed" },
        { id: "Permanent", name: "Permanent" },
        { id: "Contract", name: "Contract" },
        { id: "Temporary", name: "Temporary" },
      ],
      workModeOptions: [
        { id: "On-site", name: "On-site" },
        { id: "Remote", name: "Remote" },
        { id: "Hybrid", name: "Hybrid" },
      ],
      employmentTypeOptions: [],
      overtimeOptions: [
        { id: "true", name: "Yes" },
        { id: "false", name: "No" },
      ],
      categoryArray: [
        { name: "Supervisory", radioName: "designation", value: true },
        { name: "Non-Supervisory", radioName: "designation", value: false },
      ],
      isJobLevel: true,
      activeTab: "",
      tabsItems: [],
      tabsReady: false,
    };
  },
  watch: {
    getLevel(val) {
      this.designationCredentials.levelId = val;
      this.getLevelPayBand(val);
    },
    minLevelId(newVal){
      this.designationCredentials.minimumLevelId = newVal;
    },
    isJobLevel(newVal) {
      this.syncTabsItems();
      if (!newVal) {
        this.getAllDesignations();
      }
    },
    "designationCredentials.postType"() {
      if (
        this.activeTab !== "Position" ||
        this.isJobLevel ||
        this.isLoadingPositionForm
      ) {
        return;
      }
      this.designationCredentials.jobTitleId = null;
      this.designationCredentials.name = "";
      this.designationCredentials.jobClassificationId = null;
    },
    "designationCredentials.jobClassificationId"() {
      if (this.activeTab !== "Position" || this.isLoadingPositionForm) {
        return;
      }
      this.designationCredentials.jobTitleId = null;
      this.designationCredentials.name = "";
    },
  },
  methods: {
    setIsSupervisory(value) {
      this.designationCredentials.isSupervisory =
        value === true || value === "true";
    },
    syncTabsItems() {
      if (this.isJobLevel) {
        this.tabsItems = ["Role", "Position"];
      } else {
        const tabs = [];

        if (this.isEducationalInstitution) {
          tabs.push("Academic Post", "Non-Academic Post");
        } else {
          tabs.push("Post");
        }

        tabs.push("Administrative Post", "Position");
        this.tabsItems = tabs;
      }

      if (!this.tabsItems.includes(this.activeTab)) {
        this.activeTab = this.tabsItems[0];
      }
    },
    getPositionPrivilege(action) {
      const tab = this.activeTab;

      const jobFamilyPrivileges = {
        Role: {
          view: "jobFamily_viewRoleRegister",
          add: "jobFamily_addRole",
          import: "jobFamily_importRole",
          edit: "jobFamily_editRole",
          delete: "jobFamily_deleteRole",
        },
        Position: {
          view: "jobFamily_viewPositionRegister",
          add: "jobFamily_addPosition",
          import: "jobFamily_importPosition",
          edit: "jobFamily_editPosition",
          delete: "jobFamily_deletePosition",
        },
      };

      const jobCadrePostPrivileges = {
        view: "jobCadre_viewPostRegister",
        add: "jobCadre_addPost",
        import: "jobCadre_importPost",
        edit: "jobCadre_editPost",
        delete: "jobCadre_deletePost",
      };

      const jobCadreAdministrativePrivileges = {
        view: "jobCadre_viewAdministrativeRegister",
        add: "jobCadre_addAdministrativePost",
        import: "jobCadre_importAdministrativePost",
        edit: "jobCadre_editAdministrativePost",
        delete: "jobCadre_deleteAdministrativePost",
      };

      const jobCadrePositionPrivileges = {
        view: "jobCadre_viewPositionRegister",
        add: "jobCadre_addPosition",
        import: "jobCadre_importPosition",
        edit: "jobCadre_editPosition",
        delete: "jobCadre_deletePosition",
      };

      if (this.isJobLevel) {
        return jobFamilyPrivileges[tab]?.[action];
      }

      if (tab === "Administrative Post") {
        return jobCadreAdministrativePrivileges[action];
      }
      if (tab === "Position") {
        return jobCadrePositionPrivileges[action];
      }
      if (tab === "Academic Post" || tab === "Non-Academic Post" || tab === "Post") {
        return jobCadrePostPrivileges[action];
      }

      return null;
    },
    formattedLocationTags(selectedOption) {
      if (selectedOption === 'All Locations') {
        this.designationCredentials.locationIds = this.locationData
          .filter(opt => opt.id !== 'All Locations')
          .map(opt => opt.id);
      }
      else {
        // Update your credentials
        this.designationCredentials.locationIds = [ selectedOption ]
      }

      // Update reporting line options based on selected locations
      this.getReportingLineOptions();
    },

    removedLocationTags(removedId) {
      // Update the option's checked state
      const option = this.locationData.find(opt => opt.id === removedId)
      if (option) option.checked = false
      // Update your credentials
      this.designationCredentials.locationIds =
        this.designationCredentials.locationIds.filter(item => item !== removedId)
        if (this.designationCredentials.locationIds.length !== 0) {
          this.reportingLine = []
          return;
        }
        // Update reporting line options based on remaining locations
      getReportingLineOptions();
    },

    formattedJobTags(selectedOption) {
      // Update the option's checked state
      const option = this.jobGrades.find(opt => opt.id === selectedOption.id)
      if (option) option.checked = true
      // Update your credentials
      this.designationCredentials.jobGradeIds = this.jobGrades
        .filter(opt => opt.checked)
        .map(opt => opt.id)
    },

    removedJobTags(removedId) {
      // Update the option's checked state
      const option = this.jobGrades.find(opt => opt.id === removedId)
      if (option) option.checked = false
      // Update your credentials
      this.designationCredentials.jobGradesIds =
        this.designationCredentials.jobGradeIds.filter(item => item !== removedId)
    },


    getLevelPayBand(id, designation = null) {
      return this.$_getLevelPayBand(id).then((result) => {
        if (designation) {
          this.processPayResolutionForModal(result.data, designation);
          return;
        }

        this.payBandSetup = result.data.isSetup;
        if (!this.payBandSetup) {
          this.bandError = "No PayBand for this Job Level";
        } else if (result.data.payband) {
          this.bandError = "";
          this.max = this.convertToCurrency(result.data.payband.upperBand);
          this.min = this.convertToCurrency(result.data.payband.lowerBand);
          this.bandName = result.data.payband.name;
        }
      });
    },
    processPayResolutionForModal(data, designation) {
      this.payBandSetup = data.isSetup;

      if (!data.isSetup) {
        this.bandError = "No PayBand for this Job Level";
        this.roles = [];
        this.payBandHeader = null;
        return;
      }

      const items =
        this.compensationModel === "rangeBased"
          ? data.payGrades?.data || []
          : data.payScales?.data || [];

      if (!items.length) {
        this.payBandSetup = false;
        this.bandError =
          this.compensationModel === "rangeBased"
            ? "No pay band configured "
            : "No pay scale configured ";
        this.roles = [];
        this.payBandHeader = null;
        return;
      }

      this.bandError = "";
      this.payBandHeader = this.buildPayBandHeader(designation);
      this.roles = this.mapPayResolutionItems(items, designation);
    },
    buildPayBandHeader(designation = {}) {
      const levelId =
        designation.levelId ||
        designation.jobLevel?.id ||
        designation.level?.id ||
        null;

      return {
        title:
          designation.jobTitle?.title ||
          designation.name ||
          "------",
        level:
            designation?.jobTitle?.jobLevel?.name || "------",
      };
    },
    mapPayResolutionItems(items, designation = {}) {
      const title =
        designation.jobTitle?.title || designation.name || "------";
      const isRangeBased = this.compensationModel === "rangeBased";

      return items.map((item) => ({
        title,
        grade:
          this.resolveOptionName(item.jobGradeIds, this.jobGrades) !== "------"
            ? this.resolveOptionName(item.jobGradeIds, this.jobGrades)
            : this.resolveOptionName(item.levelIds, this.levelsArray),
        location: this.resolvePayResolutionLocation(item),
        payLabel: isRangeBased ? "Pay Range" : "Pay Scale",
        payRange: this.formatPayResolutionAmount(item, isRangeBased),
      }));
    },
    resolveOptionName(ids, options = []) {
      if (!ids?.length) {
        return "------";
      }

      const match = options.find((option) => option.id === ids[0]);
      return match?.name || "------";
    },
    resolvePayResolutionLocation(item) {
      if (item.applyToAllLocation) {
        return "All locations";
      }

      if (!item.locationIds?.length) {
        return "------";
      }

      return this.resolveOptionName(item.locationIds, this.locationData);
    },
    formatPayResolutionAmount(item, isRangeBased) {
      if (isRangeBased) {
        const { rangeMinimum, rangeMaximum } = item;

        if (rangeMinimum != null && rangeMaximum != null) {
          return `${this.convertToCurrency(rangeMinimum)} - ${this.convertToCurrency(rangeMaximum)}`;
        }

        if (rangeMinimum != null) {
          return this.convertToCurrency(rangeMinimum);
        }

        if (rangeMaximum != null) {
          return this.convertToCurrency(rangeMaximum);
        }

        return "------";
      }

      const amount =
        item.payScaleAmount ?? item.stepAmount ?? item.rangeMinimum;

      if (amount == null || amount === "") {
        return "------";
      }

      return this.convertToCurrency(amount);
    },
    closePayBandModal() {
      this.openPayBandModal = false;
      this.loadingPayBand = false;
      this.roles = [];
      this.payBandHeader = null;
      this.payBandSetup = false;
      this.bandError = "";
    },
    async handlePayBand(designation) {
      const jobTitleId =
        designation?.jobTitle?.id || designation?.jobTitleId || null;

      if (!jobTitleId) {
        this.$toasted.error("No job title found for this position", {
          duration: 4000,
        });
        return;
      }

      this.roles = [];
      this.payBandHeader = null;
      this.payBandSetup = false;
      this.bandError = "";
      this.loadingPayBand = true;
      this.openPayBandModal = true;

      try {
        await this.getLevelPayBand(jobTitleId, designation);
      } catch (error) {
        this.payBandSetup = false;
        this.bandError = "Unable to load pay information";
      } finally {
        this.loadingPayBand = false;
      }
    },
    reset() {
      this.designationCredentials = {
        name: "",
        isSupervisory: true,
        functionId: null,
        reportingTo: null,
        dottedReportingTo: null,
        enabledOvertime: null,
        eligibleForOvertime: null,
        levelId: null,
        costCentre: null,
        jobGradeIds: [],
        locationIds: [],
        jobClassificationId: null,
        maximumLevelId: null,
        minimumLevelId: null,
        postType: null,
        jobTitleId: null,
        positionCode: "",
        tenure: null,
        headCountBadge: 1,
        employmentType: null,
        workMode: null,
        fte: null,
      };
      this.min = "";
      this.editMode = false;
      this.isDotted = false;
      this.max = "";
      this.getLevel = "";
      this.minLevelId = "";
      this.payBandSetup = false;
      // this.getAllDesignations();
      this.getLocations();
      this.getJobGrades();
      this.getJobClassifications()
    },
    checkBound(firstValue, secondValue) {
      if (
        firstValue !== null ||
        firstValue !== 0 ||
        secondValue !== null ||
        secondValue !== 0
      ) {
        clearTimeout(this.timeout);
        this.timeout = setTimeout(() => {
          this.error = "";
          let valueOne = this.handleSplit(firstValue);
          let valueTwo =
            typeof secondValue === "string"
              ? this.handleSplit(secondValue)
              : secondValue;
          valueOne = Number(valueOne);
          valueTwo = Number(valueTwo);
          if (valueOne > valueTwo) {
            this.error = "Minimum value can't be higher than Maximum value";
          }
        }, 1000);
      }
    },
    async confirmDelete(id) {
      try {
        await this.$handlePrivilege("position", this.getPositionPrivilege("delete"));
        this.designationId = id;
        this.isDeletingNonPositionTitle = false;
        this.dialogDel = true;
      } catch (error) {
        this.$toasted.error("You do not have permission to perform this task", {
          duration: 5000,
        })
      }
    },
    async confirmNonPositionDelete(id) {
      try {
        await this.$handlePrivilege("position", this.getPositionPrivilege("delete"));
        this.designationId = id;
        this.isDeletingNonPositionTitle = true;
        this.dialogDel = true;
      } catch (error) {
        this.$toasted.error("You do not have permission to perform this task", {
          duration: 5000,
        });
      }
    },
    async deleteDesignation() {
      try {
        this.dialogDel = false;
        this.loadingData = true;

        if (this.isDeletingNonPositionTitle) {
          await this.$_deleteDesignationTitle(this.designationId);
          this.$toasted.success("Post deleted successfully", {
            duration: 3000,
          });
          await this.getDesignationTitle();
        } else {
          await this.$_deleteDesignation([this.designationId]);
          this.$toasted.success("Position deleted successfully", {
            duration: 3000,
          });
          await this.getDesignation("");
        }

        this.isDeletingNonPositionTitle = false;
      }
      catch (e) {
        this.$toasted.error(e.response?.data?.message, { duration: 10000 })
        this.loadingData = false
        this.isDeletingNonPositionTitle = false;
      }
    },
    async handleImport() {
      try {
        await this.$handlePrivilege("position", this.getPositionPrivilege("import"));
        this.isOpenImport = true;
      } catch (error) {
        this.$toasted.error("You do not have permission to perform this task", {
          duration: 5000,
        });
      }
    },
    async handleClick() {
      try {
        await this.$handlePrivilege("position", this.getPositionPrivilege("add"));
        if (this.activeTab === "Position") {
          await this.getDesignationTitle({ all: true });
          this.openModal = true;
          return;
        }
        this.selectedNonPositionTitle = null;
        this.editNonPositionMode = false;
        this.openNonPositionModal = true;
      } catch (error) {
        this.$toasted.error("You do not have permission to perform this task", {
          duration: 5000,
        });
      }
    },
    closeNonPositionDrawer() {
      this.openNonPositionModal = false;
      this.editNonPositionMode = false;
      this.selectedNonPositionTitle = null;
      this.loading = false;
    },
    getPostTypeMap() {
      return {
        "Administrative Post": "administrative",
        "Academic Post": "academic",
        "Non-Academic Post": "non-academic",
      };
    },
    getPostTypeFromTitleType(type) {
      const typeMap = {
        administrative: "Administrative Post",
        academic: "Academic Post",
        "non-academic": "Non-Academic Post",
      };
      return typeMap[type] || null;
    },
    resolveJobTitleId(designation = this.designationCredentials) {
      const id = designation.jobTitleId || designation.jobTitle?.id || null;
      return id ? String(id) : null;
    },
    resolvePositionName(designation = this.designationCredentials) {
      const name = designation.name?.trim();
      if (name) {
        return name;
      }

      return designation.jobTitle?.title?.trim() || "";
    },
    syncPositionNameFromJobTitle() {
      const positionName = this.resolvePositionName(this.designationCredentials);
      if (positionName) {
        this.designationCredentials.name = positionName;
      }
    },
    ensureJobTitleInList(jobTitle) {
      if (!jobTitle?.id) {
        return;
      }

      const exists = this.designationTitles.some(
        (item) => String(item.id) === String(jobTitle.id)
      );
      if (!exists) {
        this.designationTitles = [...this.designationTitles, jobTitle];
      }
    },
    withSelectedJobTitleOption(options) {
      const jobTitleId = this.resolveJobTitleId();
      if (!jobTitleId) {
        return options;
      }

      if (options.some((item) => String(item.id) === jobTitleId)) {
        return options;
      }

      const selectedTitle =
        this.designationCredentials.jobTitle ||
        this.designationTitles.find((item) => String(item.id) === jobTitleId);

      if (!selectedTitle?.title) {
        return options;
      }

      return [{ id: jobTitleId, name: selectedTitle.title }, ...options];
    },
    populatePositionForm(designation) {
      this.isLoadingPositionForm = true;

      const jobTitle = designation.jobTitle || null;
      const jobTitleId = designation.jobTitleId || jobTitle?.id || null;
      const positionName = this.resolvePositionName(designation);

      if (jobTitle) {
        this.ensureJobTitleInList(jobTitle);
      }

      Object.assign(this.designationCredentials, {
        ...designation,
        jobTitle,
        jobTitleId: jobTitleId ? String(jobTitleId) : null,
        postType:
          designation.postType ||
          this.getPostTypeFromTitleType(jobTitle?.type) ||
          null,
        jobClassificationId:
          designation.jobClassificationId ||
          jobTitle?.jobClassificationId ||
          jobTitle?.jobClassification?.id ||
          null,
        name: positionName,
      });

      this.$nextTick(() => {
        this.designationCredentials.jobTitleId = jobTitleId
          ? String(jobTitleId)
          : null;
        this.designationCredentials.name = positionName;
        this.isLoadingPositionForm = false;
        this.jobTitleSelectKey += 1;
      });
    },
    getNonPositionTitleType(payload = null) {
      if (payload?.postType) {
        return this.getPostTypeMap()[payload.postType] || null;
      }
      return this.getPostTypeMap()[this.activeTab] || null;
    },
    getDesignationTitleTemplateType() {
      if (this.activeTab === "Post") {
        return "post";
      }
      return this.getNonPositionTitleType() || "";
    },
    getTitleTypeFilter() {
      if (this.activeTab === "Position") {
        if (this.isJobLevel) {
          return null;
        }
        return this.getPostTypeMap()[this.designationCredentials.postType] || null;
      }
      if (this.activeTab === "Post") {
        return ["academic", "non-academic"];
      }
      return this.getNonPositionTitleType();
    },
    matchesTitleType(item) {
      const expectedType = this.getTitleTypeFilter();
      if (!expectedType) return true;
      if (Array.isArray(expectedType)) {
        return expectedType.includes(item.type);
      }
      return item.type === expectedType;
    },
    isAdministrativeTitleContext() {
      return (
        this.activeTab === "Administrative Post" ||
        (this.activeTab === "Position" &&
          this.designationCredentials.postType === "Administrative Post")
      );
    },
    async handleSaveNonPosition(payload) {
      try {
        this.loading = true;
        const body = {
          title: payload.title,
          jobClassificationId: payload.jobClassificationId,
          jobLevelId: payload.jobLevelId || null,
          jobGradeId: payload.jobGradeId || null,
          minimumJobLevelId: payload.minimumJobLevelId || null,
          maximumJobLevelId: payload.maximumJobLevelId || null,
          minimumJobGradeId: payload.minimumJobGradeId || null,
          category: payload.category || null,
          appointmentNature: payload.appointmentNature || null,
          appointmentTenure: payload.appointmentTenure || null,
          compensationModel: this.compensationModel,
          ...(!this.isJobLevel && { type: this.getNonPositionTitleType(payload) }),
        };

        if (payload.isEdit && payload.id) {
          await this.$_editDesignationTitle(body, payload.id);
          this.$toasted.success("Updated successfully", { duration: 3000 });
        } else {
          await this.$_createDesignationTitle(body);
          this.$toasted.success("Created successfully", { duration: 3000 });
        }

        this.closeNonPositionDrawer();
        await this.getDesignationTitle();
      } catch (error) {
        const errorMessage = error || "An error occurred while saving";
        this.$toasted.error(errorMessage, { duration: 5000 });
        this.loading = false;
      }
    },
    handleSplit(value) {
      return value.split(",").join("");
    },
    async updateDesignation() {
      this.disableBtn = true;
      if (!this.error) {
        this.designationCredentials.lowerAnnualSalaryBand = Number(
          this.handleSplit(this.min)
        );
        this.designationCredentials.upperAnnualSalaryBand = Number(
          this.handleSplit(this.max)
        );
        if (this.designationCredentials.reportingTo === "null") {
          this.designationCredentials.reportingTo = null;
        }
        if (this.designationCredentials.dottedReportingTo === "null") {
          this.designationCredentials.dottedReportingTo = null;
        }
        this.designationCredentials.enabledOvertime =
          this.designationCredentials.eligibleForOvertime;
        this.designationCredentials.id = this.designationId;
        this.syncPositionNameFromJobTitle();
        try {
          await this.$_updateDesignation(this.designationCredentials);
          this.openModal = false;
          this.$router.push({
            name: "designation",
          });
          this.$toasted.success("Position updated successfully", {
            duration: 3000,
          });
          this.getDesignation("");
          this.reset();
          this.disableBtn = false;
        } catch (error) {
          this.$toasted.error("Position not updated successfully", {
            duration: 3000,
          });
          this.disableBtn = false;
          throw new Error(error);
        }
      }
    },
    async handleNonPositionEdit(id) {
      try {
        await this.$handlePrivilege("position", this.getPositionPrivilege("edit"));
        this.selectedNonPositionTitle =
          this.designationTitles.find((item) => item.id === id) || null;
        this.editNonPositionMode = true;
        this.openNonPositionModal = true;
        this.loading = false;
      } catch (error) {
        this.$toasted.error("You do not have permission to perform this task", {
          duration: 5000,
        });
      }
    },
    async handleEdit(id) {
      this.jobGrades = this.jobGrades.map((item) => {
        item.checked = false;
        return item;
      });
      this.locationData = this.locationData.map((item) => {
        item.checked = false;
        return item;
      });
      try {
        await this.$handlePrivilege("position", this.getPositionPrivilege("edit"));
        this.loading = true;
        this.editMode = true;
        await this.getDesignationTitle({ all: true });

        const result = await this.$_getOneDesignations(id);
        const designation = result.data.designation;

        this.populatePositionForm(designation);

        if (this.designationCredentials.lowerAnnualSalaryBand) {
          this.min = this.convertToCurrency(
            this.designationCredentials.lowerAnnualSalaryBand
          );
        }
        if (this.designationCredentials.upperAnnualSalaryBand) {
          this.max = this.convertToCurrency(
            this.designationCredentials.upperAnnualSalaryBand
          );
        }
        if (this.designationCredentials.dottedReportingTo) {
          this.isDottedReportingTo = true;
          this.handleDottedReporting();
        }

        this.designationCredentials.locationIds =
          designation.orgDesignationLocations?.map(
            (item) => item.location.id
          ) || [];
        this.designationCredentials.jobGradeIds =
          designation.orgDesignationJobGrades?.map(
            (item) => item.orgJobGrade.id
          ) || [];

        this.getReportingLineOptions();

        this.jobGrades = this.jobGrades.map((item) => {
          if (this.designationCredentials.jobGradeIds.includes(item.id)) {
            item.checked = true;
          }
          return item;
        });

        this.locationData = this.locationData.map((item) => {
          if (this.designationCredentials.locationIds.includes(item.id)) {
            item.checked = true;
          }
          return item;
        });

        this.getLevel = designation.levelId;
        this.minLevelId = designation.minimumLevelId;
        this.designationId = id;
        this.loading = false;
        this.openModal = true;
      } catch (error) {
        this.loading = false;
        this.editMode = false;
        this.$toasted.error("You do not have permission to perform this task", {
          duration: 5000,
        });
      }
    },
    async createDesignation() {
      this.disableBtn = true;
      if (!this.error) {
        this.designationCredentials.lowerAnnualSalaryBand = Number(
          this.handleSplit(this.min)
        );
        this.designationCredentials.upperAnnualSalaryBand = Number(
          this.handleSplit(this.max)
        );
        if (this.designationCredentials.reportingTo === "null") {
          this.designationCredentials.reportingTo = null;
        }
        if (this.designationCredentials.dottedReportingTo === "null") {
          this.designationCredentials.dottedReportingTo = null;
        }
        this.designationCredentials.enabledOvertime =
          this.designationCredentials.eligibleForOvertime;
        this.designationCredentials.orgId = this.$AuthUser.orgId;
        this.designationCredentials.createdBy = this.$AuthUser.id;
        this.syncPositionNameFromJobTitle();
        try {
          await this.$_createDesignation(this.designationCredentials);
          this.loadingData = true;
          this.reset();
          this.$toasted.success("Position created successfully", {
            duration: 3000,
          });
          this.getDesignation("");
          this.openModal = false;
          this.disableBtn = false;
        } catch (error) {
          const errorMessage = error.response?.data?.message ||
            (error.response?.data?.errors && (
              Array.isArray(error.response.data.errors) ? error.response.data.errors[0] :
              Object.values(error.response.data.errors)[0]?.[0] || Object.values(error.response.data.errors)[0]
            )) ||
            "An error occurred while creating the position";

          this.$toasted.error(errorMessage, {
            duration: 3000,
          });
          this.disableBtn = false;
          this.reset();
        }
      }
    },
    async onTabChange(tab) {
      this.activeTab = tab;
      this.numberOfPage = 1;
      try {
        await this.$handlePrivilege("position", this.getPositionPrivilege("view"));
        this.showError = false;
        if (tab === "Position") {
          await this.getDesignationTitle({ all: true });
          this.queryUpdate();
        } else {
          this.loadingData = true;
          this.getDesignationTitle();
        }
      } catch (error) {
        this.showError = true;
        this.loadingData = false;
      }
    },
    queryUpdate() {
      const search = this.searchWord ? `&search=${this.searchWord}` : "";
      const sort = this.sortType ? `&sort=${this.sortType}` : "";
      const pageNumber = this.numberOfPage
        ? `&page=${this.numberOfPage}`
        : `&page=${this.metaData.page}`;
      const itemPage = this.itemsPerPage ? `&perPage=${this.itemsPerPage}` : "";
      this.getDesignation(`${sort}${search}${pageNumber}${itemPage}`);
    },
    async print() {
      await this.$htmlToPaper("printMe");
    },
    handleSearch(searchWord) {
      this.loadingData = true;
      if (searchWord) {
        this.searchWord = searchWord;
        this.queryUpdate();
      } else {
        this.searchWord = null;
        this.queryUpdate();
      }
    },
    handleItemsPerPage(itemNo) {
      this.itemsPerPage = itemNo;
      this.queryUpdate();
    },
    handlePage(pageNumber) {
      this.numberOfPage = pageNumber;
      this.queryUpdate();
    },
    handleSort(sortType) {
      this.loadingData = true;
      const changeSortType = sortType.split(":");
      let result;
      if (changeSortType[0] === "createdAt") {
        result = sortType;
      } else {
        result = `name:${changeSortType[1]}`;
      }
      this.sortType = result;
      this.queryUpdate();
    },
    reload() {
      this.loadingData = true;
      this.getDesignation("");
    },
    async getFunctions() {
      try {
        const response = await this.$_getFunction(this.$orgId);
        this.functions = response.data.functions;
      } catch (error) {
        throw new Error(error);
      }
    },
    handleDottedReporting() {
      this.isDotted = !this.isDotted;
    },
    getLocations(query) {
      this.$_getHumanarLocations(query).then((result) => {
        this.locationData = result.data.outlets.map(item => ({
          name: item.name,
          id: item.id,
          checked: false
        }));
        this.locationData.unshift({
          name: 'All Locations',
          id: 'All Locations',
          checked: false,
          disabled: false
        })
        this.metaData = result.data.meta;
        this.loading = false;
      });
    },
    async getLevels() {
      try {
        const response = await this.$_getLevels("");
        this.levelsArray = response.data.levels.sort((a, b) => a.rank - b.rank);
      } catch (error) {
        throw new Error(error);
      }
    },
    async getDesignation(payload) {
      try {
        const response = await this.$_getDesignationsWithPayband(payload);
        this.designationArray = clone(response.data.designations);
        this.designations = response.data.designations;
        this.metaData = response.data.meta;
        this.loadingData = false;
      } catch (error) {
        throw new Error(error);
      }
    },
    async getDesignationTitle({ all = false } = {}) {
      try {
        const titleType = all ? null : this.getNonPositionTitleType();
        const query = titleType ? `type=${titleType}` : "";
        const response = await this.$_getDesignationTitle(query);
        this.designationTitles = response.data.jobTitles;
        this.loadingData = false;
      } catch (error) {
        throw new Error(error);
      }
    },
    populateCostCenter(value) {
      const result = this.functions.find((element) => element.id === value);
      this.designationCredentials.costCentre = result?.costCenter || "";
      this.updatePositionCode();
    },
    onOrganisationUnitSelect(value) {
      this.populateCostCenter(value);
      this.getReportingLineOptions();
    },
    onJobTitleSelect(jobTitleId) {
      const normalizedId = jobTitleId ? String(jobTitleId) : null;
      const selected =
        this.designationTitles.find(
          (item) => String(item.id) === normalizedId
        ) ||
        (String(this.designationCredentials.jobTitle?.id) === normalizedId
          ? this.designationCredentials.jobTitle
          : null);
      if (!selected) {
        return;
      }
      this.designationCredentials.jobTitleId = normalizedId;
      this.designationCredentials.name = selected.title;
      if (selected.jobClassificationId) {
        this.designationCredentials.jobClassificationId = selected.jobClassificationId;
      }
      this.updatePositionCode();
    },
    updatePositionCode() {
      const postName = this.designationCredentials.name;
      const orgUnit = this.functions.find(
        (item) => item.id === this.designationCredentials.functionId
      );
      if (postName && orgUnit?.name) {
        this.designationCredentials.positionCode = `${postName} - ${orgUnit.name}`;
      }
    },
    getEmploymentTypes() {
      if (!this.$_getHumanarEmploymentTypes) {
        return;
      }
      this.$_getHumanarEmploymentTypes().then(({ data }) => {
        this.employmentTypeOptions = (data?.result || []).map((item) => ({
          id: item.name || item.id,
          name: item.name || item.id,
        }));
      });
    },
    getJobGrades(param = "") {
      this.$_getJobGrade(param).then((response) => {
        this.jobGrades = response.data.grades.map((item) => ({
          name: item.name,
          id: item.id,
          checked: false
        }));
        this.loadingData = false;
      });
    },
    getJobClassifications() {
      this.$_getJobClassifications().then((response) => {
        this.jobClassificationsOpt = response.data?.jobClassifications.map((item) => ({
          name: item.name,
          id: item.id,
          type: item.type,
        }));
      });
    },
    getAllDesignations() {
      const { functionId } = this.designationCredentials;
      let queryParams = 'source=designations';

      if (functionId) {
        queryParams += `&functionId=${functionId}`;
      }

      this.$_getDesignations(queryParams).then((response) => {
        this.reportingLine = response.data.designations
          .filter(designation => designation.isSupervisory)
          .map(({ id, positionCode }) => ({ id, name: positionCode }));
      });
    },

    // Separate method for reporting line options based on selected locations
    getReportingLineOptions() {
      const { locationIds, functionId } = this.designationCredentials;

      // if (locationIds.length === 0) {
      //   // If no locations selected, get all supervisory designations
      //   this.getAllDesignations();
      //   return;
      // }

      let queryParams = `locationIds=${locationIds.join(',')}&source=designations`;

      if (functionId) {
        queryParams += `&functionId=${functionId}`;
      }

      this.$_getDesignations(queryParams).then((response) => {
        this.reportingLine = response.data.designations
          .filter(designation => designation.isSupervisory)
          .map(({ id, positionCode }) => ({ id, name: positionCode }));
      });
    },
    async importDesignation(fileUrl, overwriteEmployee, { resolve, reject }) {
      try {
        const payload = {
          creator: this.$AuthUser.id,
          fileUrl,
        };

        let response;
        if (this.activeTab !== "Position") {
          const type = this.getDesignationTitleTemplateType();
          if (type) {
            payload.type = type;
          }
          response = await this.$_importDesignationTitle(payload);
          await this.getDesignationTitle();
        } else {
          response = await this.$_importDesignation(payload);
        }

        resolve(response);
      } catch (error) {
        reject(error);
      }
    },
    async downloadDesignationTemplate(){
      try {
        let response;
        let templateName = "Designation Template";

        if (this.activeTab !== "Position") {
          const titleType = this.getDesignationTitleTemplateType();
          response = await this.$_getDesignationTitleTemplate(titleType);
          templateName = titleType === "post"
            ? "Post Template"
            : titleType
              ? `${titleType} Post Template`
              : "Job Title Template";
        } else {
          response = await this.$_getDesignationTemplate();
        }

        const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = templateName;
        // Appended the link to the body (necessary for Firefox)
        document.body.appendChild(link);
        link.click();

        // Removed the link after download
        document.body.removeChild(link);
      } catch (error) {
          return this.$toasted.error(`Error downloading file`,{duration: 6000});
      }
    }
  },
  computed: {
    importUploadUrl() {
      const uploadPath =
        this.activeTab === "Position" ? "designations" : "job-titles";
      return `${process.env.VUE_APP_UPLOAD_CLOUDENLY_FILES}/document-management/upload/${uploadPath}`;
    },
    maxLevelOptions() {
      if (!this.minLevelId) {
        return this.levelsArray
      }

      const selectedIndex = this.levelsArray.findIndex(
        item => item.id === this.minLevelId
      )

      return this.levelsArray.map((item, index) => {
        return {
          ...item,
          disabled: index >= selectedIndex
        }
      })
    },
    filteredFunctions() {
      return this.$store.state.org.getAllFunctions;
    },
    postTypeOptions() {
      if (this.isEducationalInstitution) {
        return [
          { id: "Academic Post", name: "Academic post" },
          { id: "Non-Academic Post", name: "Non-Academic post" },
          { id: "Administrative Post", name: "Administrative post" },
        ];
      }

      return [
        { id: "Post", name: "Post" },
        { id: "Administrative Post", name: "Administrative post" },
      ];
    },
    supervisoryDesignation() {
      const hasSupervisory = this.designations.filter(item => item.isSupervisory);
      return hasSupervisory.filter(item => item.id !== this.designationCredentials.reportingTo)
    },
    filteredDesignationTitles() {
      return this.designationTitles.filter((item) => {
        if (!this.matchesTitleType(item)) {
          return false;
        }

        if (this.isAdministrativeTitleContext()) {
          return true;
        }

        const classificationType = this.isJobLevel ? "jobFamily" : "jobCadre";
        if (!item.jobClassification) return true;
        return item.jobClassification.type === classificationType;
      });
    },
    jobTitleOptions() {
      if (
        this.activeTab === "Position" &&
        !this.isJobLevel &&
        !this.designationCredentials.postType
      ) {
        return this.withSelectedJobTitleOption([]);
      }

      let titles = this.filteredDesignationTitles;
      const { jobClassificationId } = this.designationCredentials;

      if (this.activeTab === "Position" && jobClassificationId) {
        titles = titles.filter((item) => {
          const itemClassificationId =
            item.jobClassificationId || item.jobClassification?.id;
          return itemClassificationId === jobClassificationId;
        });
      }

      const options = titles.map((item) => ({
        id: String(item.id),
        name: item.title,
      }));

      return this.withSelectedJobTitleOption(options);
    },
    jobCadreOptions() {
      const cadres = this.jobClassificationsOpt.filter(
        (item) => item.type === "jobCadre"
      );
      return cadres.length ? cadres : this.jobClassificationsOpt;
    },
    jobFamilyOptions() {
      const families = this.jobClassificationsOpt.filter(
        (item) => item.type === "jobFamily"
      );
      return families.length ? families : this.jobClassificationsOpt;
    },
    isNonSupervisory() {
      return this.designationCredentials.isSupervisory === false;
    },
    postNameLabel() {
      return this.isJobLevel ? "Role Name" : "Post Name";
    },
    positionTenureOptions() {
      return this.isNonSupervisory
        ? this.nonSupervisoryTenureOptions
        : this.tenureOptions;
    },
    designationHeader() {
      return [
        { title: "Post Name", value: "name", width: "15" },
        { title: "Org unit", value: "orgFunction", width: "15" },
        { title: "Position code", value: "positionCode", width: "15" },
        { title: "Tenure", value: "tenure", width: "12" },
        { title: "Type", value: "employmentType", width: "12" },
        { title: "Reports to", value: "reportsTo", width: "15" },
        { title: "Head count", value: "headCountBadge", width: "12" },
        { title: "", value: "id", image: true },
      ];
    },
    ...mapState({
      defaultBTNColor: (state) => state.orgAppearance.defaultBtnColor,
      appearance: (state) => state.orgAppearance.orgAppearanceData,
    }),
    breadcrumbs(){
      return [
        {
          disabled: false,
          text: "Organisation Design",
          id: "",
        },
        {
          disabled: false,
          text: `Position`,
          id: ``,
        },
      ];
    },
  },
  async created() {
    try {
      const { data } = await this.$_getOrgProfile();
      this.isJobLevel = data.org.jobLevelProgression[0] === "jobLevel";
      this.isEducationalInstitution = Boolean(data?.org.isEducationalInstitution);
      this.compensationModel = data.org.compensationModel;
      this.syncTabsItems();
      this.tabsReady = true;
    } catch (error) {
      this.isEducationalInstitution = false;
      this.syncTabsItems();
      this.tabsReady = true;
    }
  },
  async mounted() {
    try {
      await this.$handlePrivilege("position", this.getPositionPrivilege("view"));
      this.showError = false;
      this.getFunctions();
      this.getLevels();
      if (this.activeTab === "Position") {
        await this.getDesignationTitle({ all: true });
        this.getDesignation("");
      } else {
        this.getDesignationTitle();
      }
      this.getLocations();
      this.getJobClassifications();
      this.getJobGrades();
      this.getEmploymentTypes();
    } catch (error) {
      this.showError = true
      this.loadingData = false;
    }
  },
};
</script>

<style scoped>
.custom-radius {
  border-radius: 7px;
}
</style>

<style>
.bg-fadeFlame {
  background-color: rgba(241, 90, 41, 0.5);
}
</style>